/**
 * Group Overview Tab Component
 * Displays group description, tags, and metadata
 */

import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import { ContentCopy as CopyIcon } from '@mui/icons-material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { AppGroup } from '../../types/group';

import { formatDistanceToNow } from '../../utils/formatting/dateUtils';
import { getSourceColor, formatGroupType } from '../../utils/groups/groupUtils';
import { useSnackbar } from '@/contexts/SnackbarContext';

interface GroupOverviewTabProps {
  group: AppGroup;
}

export const GroupOverviewTab: React.FC<GroupOverviewTabProps> = ({ group }) => {
  const { t } = useTranslation();
  const { showSuccess } = useSnackbar();

  const handleCopyExternalId = () => {
    if (group.external_id) {
      navigator.clipboard.writeText(group.external_id);
      showSuccess(t('common.copiedToClipboard'));
    }
  };

  const isExternalGroup = group.source !== 'local';

  return (
    <Box>
      <Card>
        <CardContent>
          {/* Description Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }} gutterBottom>
              {t('groups.details.description')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {group.description || (
                <em style={{ opacity: 0.7 }}>{t('groups.details.noDescription')}</em>
              )}
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Tags Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }} gutterBottom>
              {t('groups.details.tags')}
            </Typography>
            {(group.tags || []).length > 0 ? (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                {(group.tags || []).map(tag => (
                  <Chip
                    key={tag}
                    size="small"
                    label={tag}
                    variant="filled"
                  />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                <em style={{ opacity: 0.7 }}>{t('groups.details.noTags')}</em>
              </Typography>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Group Information Section */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }} gutterBottom>
              {t('groups.details.groupInformation')}
            </Typography>
            <Box component="dl" sx={{ m: 0, mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography component="dt" variant="body2" color="text.secondary">
                  {t('groups.details.source')}:
                </Typography>
                <Chip
                  component="dd"
                  size="small"
                  label={(group.source || 'unknown').toUpperCase()}
                  color={getSourceColor(group.source || 'local')}
                  variant="filled"
                />
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography component="dt" variant="body2" color="text.secondary">
                  {t('groups.details.type')}:
                </Typography>
                <Typography component="dd" variant="body2" sx={{ fontWeight: 500 }}>
                  {formatGroupType(group.type || 'custom')}
                </Typography>
              </Box>

              {group.created_at && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <Typography component="dt" variant="body2" color="text.secondary">
                    {t('groups.details.created')}:
                  </Typography>
                  <Typography component="dd" variant="body2" sx={{ fontWeight: 500 }}>
                    {formatDistanceToNow(new Date(group.created_at), { addSuffix: true })}
                  </Typography>
                </Box>
              )}

              {group.last_synced && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <Typography component="dt" variant="body2" color="text.secondary">
                    {t('groups.details.lastSynced')}:
                  </Typography>
                  <Typography component="dd" variant="body2" sx={{ fontWeight: 500 }}>
                    {formatDistanceToNow(new Date(group.last_synced), { addSuffix: true })}
                  </Typography>
                </Box>
              )}

              {group.external_id && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <Typography component="dt" variant="body2" color="text.secondary">
                    {t('groups.details.externalId')}:
                  </Typography>
                  <Box component="dd" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {group.external_id.length > 16 ? `${group.external_id.substring(0, 16)}...` : group.external_id}
                    </Typography>
                    <Tooltip title={t('common.copyToClipboard')}>
                      <IconButton size="small" onClick={handleCopyExternalId}>
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              )}

              {isExternalGroup && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography component="dt" variant="body2" color="text.secondary">
                    {t('groups.details.syncEnabled')}:
                  </Typography>
                  <Chip
                    component="dd"
                    size="small"
                    label={group.sync_enabled ? t('common.yes') : t('common.no')}
                    color={group.sync_enabled ? 'success' : 'default'}
                    variant="filled"
                  />
                </Box>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
